import { useState, useEffect } from "react";
import { useCategories } from "../hooks/useCategories";

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const uploadToCloudinary = async (file) => {
  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  const res = await fetch(url, { method: "POST", body: formData });
  if (!res.ok) throw new Error("Cloudinary upload failed");
  const data = await res.json();
  return data.secure_url;
};

const staticFields = [
  {
    label: "post_title",
    type: "text",
    required: true,
    placeholder: "Title of your post",
  },
  {
    label: "description",
    type: "textarea",
    required: true,
    placeholder: "Describe your item, service, or need",
  },
  {
    label: "contact_email",
    type: "text",
    required: false,
    placeholder: "Your email address",
  },
  { label: "relay_email", type: "checkbox", required: false, placeholder: "" },
  {
    label: "contact_name",
    type: "text",
    required: false,
    placeholder: "Your name (optional)",
  },
  {
    label: "phone_number",
    type: "text",
    required: false,
    placeholder: "Your phone number (optional)",
  },
  {
    label: "phone_extension",
    type: "text",
    required: false,
    placeholder: "Phone extension (optional)",
  },
  {
    label: "show_phone_number",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  {
    label: "phone_calls_ok",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  { label: "text_sms_ok", type: "checkbox", required: false, placeholder: "" },
  {
    label: "show_post_to_disabled_people",
    type: "checkbox",
    required: false,
    placeholder: "",
  },
  {
    label: "images",
    type: "file",
    required: false,
    placeholder: "",
    multiple: true,
  },
];

const PostForm = ({
  mode = "create",
  initialValues = {},
  categoryId,
  onSubmit,
  onCancel,
  submitLabel = "Submit",
  loading = false,
  guestEmail,
}) => {
  const { getPostFormTemplate } = useCategories();
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      setLoadingTemplate(true);
      setError(null);
      try {
        const detail = await getPostFormTemplate(categoryId);
        const fields =
          detail?.form_fields ||
          detail?.fields ||
          detail?.data?.form_fields ||
          [];
        setFormFields([...(Array.isArray(fields) ? fields : [])]);
      } catch (err) {
        setError("Failed to load post form fields.");
        setFormFields([]);
      } finally {
        setLoadingTemplate(false);
      }
    };
    if (categoryId) fetchTemplate();
  }, [categoryId, getPostFormTemplate]);

  useEffect(() => {
    // Initialize form values with all static and template fields
    const defaults = {};
    staticFields.forEach((field) => {
      if (field.type === "checkbox") {
        defaults[field.label] =
          initialValues[field.label] !== undefined
            ? !!initialValues[field.label]
            : false;
      } else if (field.type === "file") {
        if (Array.isArray(initialValues[field.label])) {
          defaults[field.label] = initialValues[field.label];
        } else if (typeof initialValues[field.label] === "string") {
          defaults[field.label] = initialValues[field.label]
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        } else {
          defaults[field.label] = [];
        }
      } else {
        defaults[field.label] = initialValues[field.label] || "";
      }
    });
    // Map attribute keys from initialValues.attributes to attr_{label} keys
    if (
      initialValues.attributes &&
      typeof initialValues.attributes === "object"
    ) {
      Object.entries(initialValues.attributes).forEach(
        ([attrKey, attrValue]) => {
          defaults[`attr_${attrKey}`] = attrValue;
        }
      );
    }
    (formFields || []).forEach((field) => {
      const key = `attr_${field.label}`;
      if (defaults[key] !== undefined) return; // Already set from attributes
      if (field.type === "checkbox") {
        defaults[key] =
          initialValues[key] !== undefined ? !!initialValues[key] : false;
      } else if (field.type === "checkbox_group") {
        defaults[key] = Array.isArray(initialValues[key])
          ? initialValues[key]
          : [];
      } else {
        defaults[key] = initialValues[key] || "";
      }
    });
    setFormValues(defaults);
  }, [initialValues, formFields]);

  const handleChange = (key, value) => {
    setFormValues((v) => ({ ...v, [key]: value }));
  };

  // File/image upload logic
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSelectedImages(files);
    setFormValues((v) => ({
      ...v,
      images: files,
    }));
  };

  const handleRemoveImage = (url) => {
    setFormValues((v) => ({
      ...v,
      images: Array.isArray(v.images)
        ? v.images.filter((img) => img !== url)
        : [],
    }));
  };

  const validateForm = () => {
    // Check static required fields
    for (const field of staticFields) {
      if (field.required && !formValues[field.label]) {
        setFormError(
          `Missing required field: ${field.label.replace(/_/g, " ")}`
        );
        return false;
      }
    }
    // Check dynamic required fields
    for (const field of formFields) {
      const key = `attr_${field.label}`;
      if (field.required) {
        if (field.type === "checkbox") {
          // Required checkbox must be checked (true)
          if (formValues[key] !== true) {
            setFormError(`Missing required field: ${field.label}`);
            return false;
          }
        } else if (
          field.type === "checkbox_group" &&
          Array.isArray(field.groups)
        ) {
          // For each group, at least one option must be selected
          for (const group of field.groups) {
            const groupKey = `attr_${field.label}${group.label}`;
            if (
              !Array.isArray(formValues[groupKey]) ||
              formValues[groupKey].length === 0
            ) {
              setFormError(`Missing required field: ${field.label}`);
              return false;
            }
          }
        } else {
          // For other types, empty string or undefined fails
          if (formValues[key] === undefined || formValues[key] === "") {
            setFormError(`Missing required field: ${field.label}`);
            return false;
          }
        }
      }
    }
    setFormError(null);
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      console.log("validation failed");
      return;
    }
    console.log("validation passed");
    setImageUploadLoading(true);
    setImageUploadError(null);
    const submitValues = { ...formValues };
    // Upload images if any are selected
    if (Array.isArray(selectedImages) && selectedImages.length > 0) {
      Promise.all(selectedImages.map((file) => uploadToCloudinary(file)))
        .then((urls) => {
          submitValues.images = urls;
          // Coerce dynamic number fields to numbers
          formFields.forEach((field) => {
            const key = `attr_${field.label}`;
            if (field.type === "number") {
              if (
                submitValues[key] === "" ||
                submitValues[key] === undefined ||
                submitValues[key] === null
              ) {
                submitValues[key] = null;
              } else {
                submitValues[key] = Number(submitValues[key]);
              }
            }
          });
          if (guestEmail) {
            submitValues.guest_email = guestEmail;
          }
          setImageUploadLoading(false);
          onSubmit(submitValues, staticFields, categoryId, formFields);
        })
        .catch((err) => {
          setImageUploadError("Failed to upload image(s) to Cloudinary.");
          setImageUploadLoading(false);
        });
    } else {
      // No images to upload
      submitValues.images = [];
      formFields.forEach((field) => {
        const key = `attr_${field.label}`;
        if (field.type === "number") {
          if (
            submitValues[key] === "" ||
            submitValues[key] === undefined ||
            submitValues[key] === null
          ) {
            submitValues[key] = null;
          } else {
            submitValues[key] = Number(submitValues[key]);
          }
        }
      });
      if (guestEmail) {
        submitValues.guest_email = guestEmail;
      }
      setImageUploadLoading(false);
      onSubmit(submitValues, staticFields, categoryId, formFields);
    }
  };

  if (loadingTemplate) return <div>Loading form...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {formError && (
        <div className="text-red-600 text-sm mb-2">{formError}</div>
      )}
      {/* Ensure guest_email is always included if provided */}
      {guestEmail && <input disabled name="guest_email" value={guestEmail} />}
      {formFields.length > 0 && (
        <div className="pt-2 pb-6 border-b border-gray-300 mb-6">
          <div className="font-semibold text-xs mb-2 text-gray-700 uppercase tracking-wider">
            Additional Information
          </div>
          {formFields.map((field, idx) => (
            <div key={idx} className="flex items-center gap-4 mb-2">
              <label className="block text-sm mb-0 min-w-[160px] text-gray-700 text-right pr-2">
                {field.label.toLowerCase()}
                {field.required && <span className="text-red-500"> *</span>}
              </label>
              <div className="flex-1">
                {field.type === "text" && (
                  <input
                    type="text"
                    className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                    placeholder={field.placeholder || ""}
                    required={field.required}
                    value={formValues[`attr_${field.label}`] || ""}
                    onChange={(e) =>
                      handleChange(`attr_${field.label}`, e.target.value)
                    }
                  />
                )}
                {field.type === "number" && (
                  <input
                    type="number"
                    className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                    placeholder={field.placeholder || ""}
                    required={field.required}
                    value={formValues[`attr_${field.label}`] || ""}
                    onChange={(e) =>
                      handleChange(`attr_${field.label}`, e.target.value)
                    }
                  />
                )}
                {field.type === "dropdown" && (
                  <select
                    className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                    required={field.required}
                    value={formValues[`attr_${field.label}`] || ""}
                    onChange={(e) =>
                      handleChange(`attr_${field.label}`, e.target.value)
                    }
                  >
                    <option value="">select...</option>
                    {field.options?.map((opt, i) => (
                      <option key={i} value={opt.value}>
                        {opt.label.toLowerCase()}
                      </option>
                    ))}
                  </select>
                )}
                {field.type === "radio" && (
                  <div className="flex flex-wrap gap-4">
                    {field.options?.map((opt, i) => (
                      <label key={i} className="inline-flex items-center mr-4">
                        <input
                          type="radio"
                          name={`attr_${field.label}`}
                          value={opt.value}
                          checked={
                            formValues[`attr_${field.label}`] === opt.value
                          }
                          required={field.required}
                          onChange={(e) =>
                            handleChange(`attr_${field.label}`, e.target.value)
                          }
                        />
                        <span className="ml-1 text-base">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
                {field.type === "checkbox" && (
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={!!formValues[`attr_${field.label}`]}
                    onChange={(e) =>
                      handleChange(`attr_${field.label}`, e.target.checked)
                    }
                  />
                )}
                {field.type === "checkbox_group" && (
                  <div className="space-y-2">
                    {field.groups?.map((group, gIdx) => (
                      <div key={gIdx} className="mb-2">
                        <div className="text-sm mb-1">
                          {group.label.toLowerCase()}
                        </div>
                        <div className="space-y-1">
                          {group.options?.map((opt, oIdx) => (
                            <label
                              key={oIdx}
                              className="flex items-center text-sm"
                            >
                              <input
                                type="checkbox"
                                className="mr-2"
                                checked={
                                  Array.isArray(
                                    formValues[
                                      `attr_${field.label}${group.label}`
                                    ]
                                  ) &&
                                  formValues[
                                    `attr_${field.label}${group.label}`
                                  ].includes(opt.value)
                                }
                                onChange={(e) => {
                                  const key = `attr_${field.label}${group.label}`;
                                  const arr = Array.isArray(formValues[key])
                                    ? formValues[key]
                                    : [];
                                  if (e.target.checked) {
                                    handleChange(key, [...arr, opt.value]);
                                  } else {
                                    handleChange(
                                      key,
                                      arr.filter((val) => val !== opt.value)
                                    );
                                  }
                                }}
                              />
                              {opt.label.toLowerCase()}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {field.type === "file" && (
                  <input
                    type="file"
                    className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                    required={field.required}
                    multiple={field.multiple}
                    onChange={handleImageUpload}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Render static fields */}
      <div className="font-semibold text-xs mb-2 text-gray-700 uppercase tracking-wider mt-6">
        Main Details
      </div>
      {staticFields.map((field, idx) => (
        <div key={field.label} className="flex items-center gap-4 mb-2">
          <label className="block text-sm mb-0 min-w-[160px] text-gray-700 text-right pr-2">
            {field.label.replace(/_/g, " ").toLowerCase()}
            {field.required && <span className="text-red-500"> *</span>}
          </label>
          <div className="flex-1">
            {field.type === "checkbox" ? (
              <input
                type="checkbox"
                className="mr-2"
                checked={!!formValues[field.label]}
                onChange={(e) => handleChange(field.label, e.target.checked)}
              />
            ) : field.type === "file" ? (
              <>
                <input
                  type="file"
                  multiple={field.multiple}
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                />
                {imageUploadLoading && (
                  <div className="text-xs text-gray-600 mt-1">Uploading...</div>
                )}
                {imageUploadError && (
                  <div className="text-xs text-red-600 mt-1">
                    {imageUploadError}
                  </div>
                )}
                {Array.isArray(selectedImages) && selectedImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Array.from(selectedImages).map((file, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt="selected"
                          className="h-16 w-16 object-cover border rounded"
                        />
                        <button
                          type="button"
                          className="absolute top-0 right-0 bg-white bg-opacity-80 rounded-full px-1 text-xs text-red-600 group-hover:bg-opacity-100"
                          onClick={() => {
                            const newFiles = Array.from(selectedImages);
                            newFiles.splice(i, 1);
                            setSelectedImages(newFiles);
                            setFormValues((v) => ({
                              ...v,
                              images: newFiles,
                            }));
                          }}
                          style={{ lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : field.type === "textarea" ? (
              <textarea
                className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                placeholder={field.placeholder || ""}
                required={field.required}
                value={formValues[field.label] || ""}
                onChange={(e) => handleChange(field.label, e.target.value)}
                rows={4}
              />
            ) : (
              <input
                type={field.type}
                className="border border-gray-300 px-2 py-1 w-full max-w-md text-sm focus:outline-none focus:border-gray-500"
                placeholder={field.placeholder || ""}
                required={field.required}
                value={formValues[field.label] || ""}
                onChange={(e) => handleChange(field.label, e.target.value)}
              />
            )}
          </div>
        </div>
      ))}
      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <button
            type="button"
            className="px-4 py-2 rounded bg-gray-200 text-gray-700"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          disabled={loading}
        >
          {loading ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default PostForm;
